import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import ChatRoomList, { ChatRoomListRef } from '../components/chat/ChatRoomList';
import ChatRoomView from '../components/chat/ChatRoomView';
import { apiService } from '../services/api';
import { ChatRoom, ChatRoomsResponse } from '../types';
import { useSSE } from '../hooks/useWebSocket';
import { envConfig } from '../config/env';
import { Mail } from 'lucide-react'; // Added Mail icon import

const ChatRoomPage: React.FC = () => {
  const { userEmail } = useParams<{ userEmail?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [chatRooms, setChatRooms] = useState<ChatRoomsResponse>({ unreplied: [], replied: [], has_new: [] });
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(userEmail || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 聊天室列表宽度相关状态
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // 从本地存储读取用户上次设置的宽度，默认320px
    const savedWidth = localStorage.getItem('chatroom-sidebar-width');
    return savedWidth ? parseInt(savedWidth, 10) : 320;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // 快速滚动功能的ref
  const chatRoomListRef = useRef<ChatRoomListRef>(null);
  
  // 添加去重缓存，防止短时间内重复处理相同事件
  const processedEventsRef = useRef<Map<string, number>>(new Map());
  const EVENT_CACHE_DURATION = 10000; // 10秒内相同事件视为重复

  // 快速滚动函数
  const scrollToSection = (section: 'unreplied' | 'replied' | 'has_new') => {
    chatRoomListRef.current?.scrollToSection(section);
  };
  
  // 检查事件是否已处理（防重复）
  const isEventProcessed = (eventKey: string): boolean => {
    const now = Date.now();
    const lastProcessed = processedEventsRef.current.get(eventKey);
    
    if (lastProcessed && (now - lastProcessed) < EVENT_CACHE_DURATION) {
      return true; // 事件已在缓存期内处理过
    }
    
    // 清理过期的缓存条目
    const keysToDelete: string[] = [];
    processedEventsRef.current.forEach((timestamp, key) => {
      if ((now - timestamp) >= EVENT_CACHE_DURATION) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => processedEventsRef.current.delete(key));
    
    // 记录新事件
    processedEventsRef.current.set(eventKey, now);
    return false;
  };

  // 统一确定用户邮箱的逻辑
  const determineUserEmailFromEvent = (eventData: any): string => {
    // 如果事件中直接包含user_email，优先使用
    if (eventData.user_email) {
      return eventData.user_email;
    }
    
    // 根据邮件类型确定用户邮箱
    if (eventData.email_type === 'received') {
      // 收到的邮件，用户邮箱是发件人
      return eventData.sender_email;
    } else if (eventData.email_type === 'sent') {
      // 发送的邮件，用户邮箱是收件人
      return eventData.recipient_email;
    }
    
    // 兜底逻辑：如果只有sender_email，则使用它（通常适用于批量操作）
    return eventData.sender_email;
  };

  // 实现与后端一致的聊天室分类逻辑
  const classifyChatRoom = (latestUserTime: string | null, latestReplyTime: string | null): keyof ChatRoomsResponse => {
    // 解析时间为Date对象，便于比较
    const parseTime = (timeStr: string | null): Date | null => {
      if (!timeStr) return null;
      try {
        return new Date(timeStr);
      } catch {
        return null;
      }
    };

    const userTime = parseTime(latestUserTime);
    const replyTime = parseTime(latestReplyTime);

    if (userTime && !replyTime) {
      // 有用户邮件，无回复邮件 -> 未回复
      return 'unreplied';
    } else if (userTime && replyTime) {
      // 有用户邮件，有回复邮件 -> 比较时间
      if (replyTime >= userTime) {
        return 'replied';
      } else {
        return 'has_new';
      }
    } else if (!userTime && replyTime) {
      // 无用户邮件，有回复邮件 -> 已回复
      return 'replied';
    } else {
      // 既无用户邮件，也无回复邮件 -> 未回复
      return 'unreplied';
    }
  };

  // 智能判断是否需要重新分类聊天室
  const shouldReclassifyChatRoom = (room: ChatRoom, currentCategory: keyof ChatRoomsResponse, oldStatus: string, newStatus: string): boolean => {
    // 只有当状态从 unread 变为 read 时才考虑重新分类
    if (oldStatus !== 'unread' || newStatus !== 'read') {
      return false;
    }
    
    // 如果当前在 replied 分类，且有回复邮件，则不需要重新分类
    if (currentCategory === 'replied' && (room.reply_emails_count || 0) > 0) {
      return false;
    }
    
    // 如果当前在 unreplied 分类，且没有回复邮件，只需要清除未读标识，不需要重新分类
    if (currentCategory === 'unreplied' && (room.reply_emails_count || 0) === 0) {
      return false;
    }
    
    // 其他情况需要重新分类
    return true;
  };

  // 拖拽调整宽度的处理函数
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + deltaX)); // 限制宽度在200px-600px之间
      setSidebarWidth(newWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // 保存宽度到本地存储
      localStorage.setItem('chatroom-sidebar-width', sidebarWidth.toString());
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth]);

  // 双击重置宽度到默认值
  const handleDoubleClick = useCallback(() => {
    setSidebarWidth(320);
    localStorage.setItem('chatroom-sidebar-width', '320');
  }, []);

  // 保存宽度变化到本地存储
  useEffect(() => {
    localStorage.setItem('chatroom-sidebar-width', sidebarWidth.toString());
  }, [sidebarWidth]);

  // 加载聊天室列表
  const loadChatRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== 开始加载聊天室 ===');
      console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
      
      const response = await apiService.get('/sender-rules/chats');
      console.log('=== API响应成功 ===');
      console.log('Status:', response.status);
      console.log('Data:', response.data);
      console.log('Data type:', typeof response.data);
      console.log('Data:', response.data?.unreplied);
      console.log('Data:', response.data?.replied);
      console.log('Data:', response.data?.has_new);
      
      // 检测和清理重复的聊天室（先分类内去重，再全局去重）
      const localCleanedData = {
        unreplied: removeDuplicateRooms(response.data?.unreplied || []),
        replied: removeDuplicateRooms(response.data?.replied || []),
        has_new: removeDuplicateRooms(response.data?.has_new || [])
      };
      
      // 全局去重检查
      const globalCleanedData = removeGlobalDuplicates(localCleanedData);
      
      // 检查是否有重复被清理
      const originalCount = (response.data?.unreplied?.length || 0) + (response.data?.replied?.length || 0) + (response.data?.has_new?.length || 0);
      const finalCount = globalCleanedData.unreplied.length + globalCleanedData.replied.length + globalCleanedData.has_new.length;
      
      if (originalCount !== finalCount) {
        console.warn(`⚠️ 检测到并清理了 ${originalCount - finalCount} 个重复聊天室`);
      }
      
      // 直接设置数据，不做额外验证
      setChatRooms(globalCleanedData);
      console.log('=== 聊天室数据设置完成 ===');
      
      // 如果有选中的用户邮箱，自动切换到对应的tab
      if (selectedUserEmail) {
        setTimeout(() => {
          chatRoomListRef.current?.scrollToUserEmail(selectedUserEmail);
        }, 100); // 短暂延迟确保DOM更新完成
      }
      
    } catch (err: any) {
      console.error('=== API调用失败 ===');
      console.error('Error:', err);
      console.error('Error message:', err?.message);
      console.error('Error response:', err?.response);
      console.error('Error status:', err?.response?.status);
      console.error('Error data:', err?.response?.data);
      
      const errorMessage = err?.response?.data?.detail || err?.message || '未知错误';
      setError(`加载聊天室失败: ${errorMessage}`);
    } finally {
      setLoading(false);
      console.log('=== 加载聊天室完成 ===');
    }
  };

  // 全局去重函数 - 确保每个用户邮箱只出现在一个分类中
  const deduplicateAllChatRooms = (rooms: ChatRoomsResponse): ChatRoomsResponse => {
    const seenEmails = new Set<string>();
    const result: ChatRoomsResponse = { unreplied: [], replied: [], has_new: [] };
    
    // 按优先级顺序处理：has_new > unreplied > replied
    const categories: (keyof ChatRoomsResponse)[] = ['has_new', 'unreplied', 'replied'];
    
    for (const category of categories) {
      const categoryRooms = rooms[category] || [];
      for (const room of categoryRooms) {
        if (!seenEmails.has(room.user_email)) {
          seenEmails.add(room.user_email);
          result[category].push(room);
        } else {
          console.warn(`🗑️ 全局去重：移除重复聊天室 ${room.user_email} 在 ${category} 分类中的条目`);
        }
      }
    }
    
    return result;
  };

  // 辅助函数：移除重复的聊天室
  const removeDuplicateRooms = (rooms: ChatRoom[]): ChatRoom[] => {
    const seen = new Set<string>();
    const uniqueRooms: ChatRoom[] = [];
    
    for (const room of rooms) {
      if (!seen.has(room.user_email)) {
        seen.add(room.user_email);
        uniqueRooms.push(room);
      } else {
        console.warn(`🗑️ 移除重复聊天室: ${room.user_email} (ID: ${room.id})`);
      }
    }
    
    return uniqueRooms;
  };

  // 辅助函数：全局去重检查
  const removeGlobalDuplicates = (data: ChatRoomsResponse): ChatRoomsResponse => {
    const seenEmails = new Set<string>();
    const result: ChatRoomsResponse = { unreplied: [], replied: [], has_new: [] };
    
    // 按优先级顺序处理：has_new > unreplied > replied
    const categories: (keyof ChatRoomsResponse)[] = ['has_new', 'unreplied', 'replied'];
    
    for (const category of categories) {
      const rooms = data[category] || [];
      for (const room of rooms) {
        if (!seenEmails.has(room.user_email)) {
          seenEmails.add(room.user_email);
          result[category].push(room);
        } else {
          console.warn(`🗑️ 全局去重：移除重复聊天室 ${room.user_email} 在 ${category} 分类中的条目`);
        }
      }
    }
    
    return result;
  };

  // 选择聊天室
  const handleSelectChatRoom = (userEmail: string) => {
    setSelectedUserEmail(userEmail);
    navigate(`/chat/${encodeURIComponent(userEmail)}`);
    
    // 用户点击时不自动滚动，避免打断浏览体验
  };

  // 更新聊天室数据
  const handleUpdateChatRoom = (userEmail: string, updates: Partial<ChatRoom>) => {
    setChatRooms(prevRooms => {
      const newRooms = { ...prevRooms };
      
      // 查找并更新对应的聊天室
      for (const [category, rooms] of Object.entries(newRooms) as [keyof ChatRoomsResponse, ChatRoom[]][]) {
        const roomIndex = rooms.findIndex(r => r.user_email === userEmail);
        if (roomIndex !== -1) {
          const updatedRoom = { ...rooms[roomIndex], ...updates };
          newRooms[category] = [
            ...rooms.slice(0, roomIndex),
            updatedRoom,
            ...rooms.slice(roomIndex + 1)
          ];
          break;
        }
      }
      
      // 应用全局去重，确保没有重复条目
      return deduplicateAllChatRooms(newRooms);
    });
  };

  useEffect(() => {
    loadChatRooms();
  }, []);

  // 定期清理重复聊天室的保护机制
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setChatRooms(prevRooms => {
        const cleanedRooms = deduplicateAllChatRooms(prevRooms);
        
        // 检查是否有重复被清理
        const originalCount = (prevRooms.unreplied?.length || 0) + (prevRooms.replied?.length || 0) + (prevRooms.has_new?.length || 0);
        const cleanedCount = cleanedRooms.unreplied.length + cleanedRooms.replied.length + cleanedRooms.has_new.length;
        
        if (originalCount !== cleanedCount) {
          console.warn(`🧹 定期清理检测到并移除了 ${originalCount - cleanedCount} 个重复聊天室`);
        }
        
        return cleanedRooms;
      });
    }, 30000); // 每30秒检查一次
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // 如果有URL参数但没有选中的用户邮箱，设置选中状态
  useEffect(() => {
    if (userEmail && !selectedUserEmail) {
      const decodedEmail = decodeURIComponent(userEmail);
      setSelectedUserEmail(decodedEmail);
      
      // 自动切换到对应的tab
      setTimeout(() => {
        chatRoomListRef.current?.scrollToUserEmail(decodedEmail);
      }, 200); // 稍长的延迟确保聊天室数据已加载
    }
  }, [userEmail, selectedUserEmail]);

  // 处理查询参数中的sender
  useEffect(() => {
    const senderFromQuery = searchParams.get('sender');
    if (senderFromQuery && !selectedUserEmail) {
      setSelectedUserEmail(senderFromQuery);
      // 更新URL为标准格式
      navigate(`/chat/${encodeURIComponent(senderFromQuery)}`, { replace: true });
      
      // 自动切换到对应的tab
      setTimeout(() => {
        chatRoomListRef.current?.scrollToUserEmail(senderFromQuery);
      }, 200); // 稍长的延迟确保聊天室数据已加载
    }
  }, [searchParams, selectedUserEmail, navigate]);

  // SSE连接用于实时更新聊天室列表
  const { connectionStatus } = useSSE({
    url: `${envConfig.API_BASE_URL}/sse/emails`,
    onOpen: () => {
      console.log('✅ SSE连接已建立，开始监听邮件事件');
    },
    onClose: () => {
      console.log('⚠️ SSE连接已断开');
    },
    onError: (error) => {
      console.error('❌ SSE连接错误:', error);
    },
    onMessage: (message) => {
      console.log('聊天室页面收到SSE消息:', message);
      console.log('消息类型:', message.type);
      console.log('消息数据:', JSON.stringify(message.data, null, 2));
      
      if (message.type === 'new_email' || message.type === 'new_sent_email') {
        // 收到新邮件通知，智能更新聊天室列表
        console.log('收到新邮件通知，智能更新聊天室:', message.data);
        
        const emailData = message.data;
        const userEmail = message.type === 'new_email' ? emailData.sender_email : emailData.recipient_email;
        
        // 对于new_sent_email事件，添加防重复处理逻辑
        if (message.type === 'new_sent_email') {
          console.log(`🔍 处理发送邮件事件 - 用户: ${userEmail}, 邮件ID: ${emailData.id}, Message-ID: ${emailData.message_id}`);
          
          // 检查是否为重复事件
          const eventKey = `sent_email_${userEmail}_${emailData.message_id || emailData.id}`;
          if (isEventProcessed(eventKey)) {
            console.log(`⚠️ 跳过重复的发送邮件事件: ${userEmail} (事件键: ${eventKey})`);
            return; // 直接跳过处理
          }
          console.log(`✅ 新的发送邮件事件，继续处理: ${userEmail} (事件键: ${eventKey})`);
        }
        
        // 智能更新聊天室数据，防止重复
        setChatRooms(prevRooms => {
          const newRooms = { ...prevRooms };
          
          // 查找是否已存在该用户的聊天室
          let existingRoom: ChatRoom | null = null;
          let existingCategory: keyof ChatRoomsResponse | null = null;
          
          console.log(`🔍 查找现有聊天室: ${userEmail}`);
          console.log('当前聊天室状态:', {
            unreplied: newRooms.unreplied?.length || 0,
            replied: newRooms.replied?.length || 0,
            has_new: newRooms.has_new?.length || 0
          });
          
          // 先检查是否存在重复条目并清理
          const allCategories: (keyof ChatRoomsResponse)[] = ['unreplied', 'replied', 'has_new'];
          let foundCount = 0;
          
          for (const category of allCategories) {
            const rooms = newRooms[category] || [];
            const matchingRooms = rooms.filter(r => r.user_email === userEmail);
            
            if (matchingRooms.length > 0) {
              foundCount += matchingRooms.length;
              if (!existingRoom) {
                // 使用第一个找到的作为现有聊天室
                existingRoom = matchingRooms[0];
                existingCategory = category;
              }
              
              // 如果发现重复，只保留第一个
              if (matchingRooms.length > 1) {
                console.warn(`⚠️ 发现重复聊天室: ${userEmail} 在 ${category} 分类中有 ${matchingRooms.length} 个条目`);
                newRooms[category] = rooms.filter(r => r.user_email !== userEmail);
                if (existingCategory === category) {
                  newRooms[category].push(existingRoom);
                }
              }
            }
          }
          
          if (foundCount > 1) {
            console.warn(`⚠️ 总共发现 ${foundCount} 个重复聊天室条目: ${userEmail}，已清理`);
          }
          
          if (existingRoom && existingCategory) {
            console.log(`✅ 找到现有聊天室: ${userEmail} 在 ${existingCategory} 分类, ID: ${existingRoom.id}`);
          }
          
          if (existingRoom && existingCategory) {
            // 更新现有聊天室
            const updatedRoom = {
              ...existingRoom,
              total_emails: existingRoom.total_emails + 1,
              latest_user_email_time: message.type === 'new_email' ? emailData.received_date || emailData.sent_date : existingRoom.latest_user_email_time,
              latest_reply_email_time: message.type === 'new_sent_email' ? emailData.sent_date || emailData.received_date : existingRoom.latest_reply_email_time,
              update_time: new Date().toISOString()
            };
            
            if (message.type === 'new_email') {
              updatedRoom.user_emails_count = (existingRoom.user_emails_count || 0) + 1;
              updatedRoom.has_unread_user_email = true;
            } else {
              updatedRoom.reply_emails_count = (existingRoom.reply_emails_count || 0) + 1;
            }
            
            // 移除旧位置的聊天室
            newRooms[existingCategory] = newRooms[existingCategory].filter(r => r.user_email !== userEmail);
            
            // 使用统一的分类逻辑决定新的分类
            const newCategory = classifyChatRoom(
              updatedRoom.latest_user_email_time || null, 
              updatedRoom.latest_reply_email_time || null
            );
            
            console.log(`📧 新邮件事件处理: ${userEmail}`);
            console.log(`   最新用户邮件时间: ${updatedRoom.latest_user_email_time}`);
            console.log(`   最新回复邮件时间: ${updatedRoom.latest_reply_email_time}`);
            console.log(`   分类结果: ${existingCategory} -> ${newCategory}`);
            
            // 添加到新分类，并保持按时间排序
            if (!newRooms[newCategory]) {
              newRooms[newCategory] = [];
            }
            
            // 确保新分类中不存在重复条目
            const existsInNewCategory = newRooms[newCategory].some(r => r.user_email === userEmail);
            if (!existsInNewCategory) {
              newRooms[newCategory].unshift(updatedRoom);
            } else {
              console.warn(`⚠️ 目标分类 ${newCategory} 中已存在聊天室: ${userEmail}，跳过添加`);
            }
            
            console.log(`✅ 聊天室已智能更新: ${userEmail} 从 ${existingCategory} 移到 ${newCategory}`);
            
            // SSE更新时不自动滚动，避免打断用户浏览
          } else {
            // 没有找到现有聊天室，这可能是时序问题
            console.warn(`⚠️ 未找到现有聊天室: ${userEmail}，可能是时序问题`);
            console.warn('跳过此次更新，避免重复条目');
            
            // 不要触发刷新，避免竞态条件导致重复
            // 下次正常的用户操作或定时刷新会自动同步数据
          }
          
          // 应用全局去重，确保没有重复条目
          return deduplicateAllChatRooms(newRooms);
        });
        
        // 移除延迟刷新逻辑，只依赖智能更新
        console.log('✅ 聊天室智能更新完成，无需完整刷新');
        
      } else if (message.type === 'email_status_changed') {
        // 邮件状态变更事件
        console.log('收到邮件状态变更通知:', message.data);
        
        const emailData = message.data;
        // 统一确定用户邮箱的逻辑
        const userEmail = determineUserEmailFromEvent(emailData);
        
        // 智能更新聊天室状态
        setChatRooms(prevRooms => {
          const newRooms = { ...prevRooms };
          
          // 查找对应的聊天室
          for (const [category, rooms] of Object.entries(newRooms) as [keyof ChatRoomsResponse, ChatRoom[]][]) {
            const roomIndex = rooms.findIndex((r: ChatRoom) => r.user_email === userEmail);
            if (roomIndex !== -1) {
              const room = rooms[roomIndex];
              
              // 根据状态变更重新分类聊天室
              if (emailData.old_status === 'unread' && emailData.status === 'read') {
                console.log(`📧 邮件状态变更: ${userEmail} 从 ${emailData.old_status} 变为 ${emailData.status}，当前分类: ${category}`);
                
                // 邮件被标记为已读，更新聊天室信息
                const updatedRoom: ChatRoom = {
                  ...room,
                  has_unread_user_email: false,  // 标记为已读后，清除新邮件标识
                  update_time: new Date().toISOString()
                };
                
                // 智能判断是否需要重新分类
                if (shouldReclassifyChatRoom(room, category, emailData.old_status, emailData.status)) {
                  console.log(`🔄 需要重新分类聊天室: ${userEmail} 从 ${category}`);
                  
                  // 移除旧位置
                  newRooms[category] = rooms.filter((_, index) => index !== roomIndex);
                  
                  // 优先使用后端提供的完整时间信息进行分类
                  const latestUserTime = emailData.latest_user_email_time || updatedRoom.latest_user_email_time;
                  const latestReplyTime = emailData.latest_reply_email_time || updatedRoom.latest_reply_email_time;
                  
                  // 使用统一的分类逻辑决定新的分类
                  const targetCategory = classifyChatRoom(latestUserTime || null, latestReplyTime || null);
                  
                  console.log(`📧 状态变更事件处理: ${userEmail}`);
                  console.log(`   后端提供的最新用户邮件时间: ${emailData.latest_user_email_time}`);
                  console.log(`   后端提供的最新回复邮件时间: ${emailData.latest_reply_email_time}`);
                  console.log(`   使用的最新用户邮件时间: ${latestUserTime}`);
                  console.log(`   使用的最新回复邮件时间: ${latestReplyTime}`);
                  console.log(`   分类结果: ${category} -> ${targetCategory}`);
                  
                  // 确保目标分类中不存在重复条目
                  const existsInTarget = newRooms[targetCategory].some(r => r.user_email === userEmail);
                  if (!existsInTarget) {
                    newRooms[targetCategory] = [updatedRoom, ...newRooms[targetCategory]];
                    console.log(`✅ 聊天室已移动: ${userEmail} 从 ${category} 到 ${targetCategory}`);
                  } else {
                    console.warn(`⚠️ 目标分类 ${targetCategory} 中已存在聊天室: ${userEmail}，跳过添加`);
                    // 如果目标分类已存在，至少更新现有条目
                    const existingIndex = newRooms[targetCategory].findIndex(r => r.user_email === userEmail);
                    if (existingIndex !== -1) {
                      newRooms[targetCategory][existingIndex] = updatedRoom;
                      console.log(`✅ 更新现有聊天室: ${userEmail} 在 ${targetCategory} 分类中`);
                    }
                  }
                } else {
                  // 不需要重新分类，只需要在原分类中更新聊天室信息
                  console.log(`📝 只更新聊天室状态，不重新分类: ${userEmail} 在 ${category} 分类中`);
                  newRooms[category][roomIndex] = updatedRoom;
                }
                
                // SSE更新时不自动滚动，避免打断用户浏览
              }
              
              break;
            }
          }
          
          // 应用全局去重，确保没有重复条目
          return deduplicateAllChatRooms(newRooms);
        });
        
      } else if (message.type === 'emails_bulk_status_changed') {
        // 批量邮件状态变更事件
        console.log('收到批量邮件状态变更通知:', message.data);
        
        const { old_status, new_status } = message.data;
        // 统一确定用户邮箱的逻辑
        const userEmail = determineUserEmailFromEvent(message.data);
        
        // 智能更新对应聊天室
        setChatRooms(prevRooms => {
          const newRooms = { ...prevRooms };
          
          // 查找对应的聊天室
          for (const [category, rooms] of Object.entries(newRooms) as [keyof ChatRoomsResponse, ChatRoom[]][]) {
            const roomIndex = rooms.findIndex((r: ChatRoom) => r.user_email === userEmail);
            if (roomIndex !== -1) {
              const room = rooms[roomIndex];
              
              // 批量标记为已读
              if (old_status === 'unread' && new_status === 'read') {
                console.log(`📧 批量邮件状态变更: ${userEmail} 从 ${old_status} 变为 ${new_status}，当前分类: ${category}`);
                
                const updatedRoom: ChatRoom = {
                  ...room,
                  has_unread_user_email: false,
                  update_time: new Date().toISOString()
                };
                
                // 移除旧位置
                newRooms[category] = rooms.filter((_, index) => index !== roomIndex);
                
                // 优先使用后端提供的完整时间信息进行分类
                const latestUserTime = message.data.latest_user_email_time || updatedRoom.latest_user_email_time;
                const latestReplyTime = message.data.latest_reply_email_time || updatedRoom.latest_reply_email_time;
                
                // 使用统一的分类逻辑决定新的分类
                const targetCategory = classifyChatRoom(latestUserTime || null, latestReplyTime || null);
                
                console.log(`📧 批量状态变更事件处理: ${userEmail}`);
                console.log(`   后端提供的最新用户邮件时间: ${message.data.latest_user_email_time}`);
                console.log(`   后端提供的最新回复邮件时间: ${message.data.latest_reply_email_time}`);
                console.log(`   使用的最新用户邮件时间: ${latestUserTime}`);
                console.log(`   使用的最新回复邮件时间: ${latestReplyTime}`);
                console.log(`   分类结果: ${category} -> ${targetCategory}`);
                
                // 确保目标分类中不存在重复条目
                const existsInTarget = newRooms[targetCategory].some(r => r.user_email === userEmail);
                if (!existsInTarget) {
                  newRooms[targetCategory] = [updatedRoom, ...newRooms[targetCategory]];
                  console.log(`✅ 批量操作：聊天室已移动: ${userEmail} 从 ${category} 到 ${targetCategory}`);
                } else {
                  console.warn(`⚠️ 批量操作：目标分类 ${targetCategory} 中已存在聊天室: ${userEmail}，跳过添加`);
                  // 如果目标分类已存在，至少更新现有条目
                  const existingIndex = newRooms[targetCategory].findIndex(r => r.user_email === userEmail);
                  if (existingIndex !== -1) {
                    newRooms[targetCategory][existingIndex] = updatedRoom;
                    console.log(`✅ 批量操作：更新现有聊天室: ${userEmail} 在 ${targetCategory} 分类中`);
                  }
                }
                
                // SSE更新时不自动滚动，避免打断用户浏览
              }
              
              break;
            }
          }
          
          // 应用全局去重，确保没有重复条目
          return deduplicateAllChatRooms(newRooms);
        });
        
      } else if (message.type === 'ai_analysis_completed') {
        // AI分析完成，可以在聊天室中显示AI分析结果
        const emailData = message.data;
        console.log('✅ AI分析完成:', emailData);
        // 这里可以添加AI分析完成的UI提示
      } else if (message.type === 'ai_analysis_failed') {
        // AI分析失败
        const emailData = message.data;
        console.log('❌ AI分析失败:', emailData);
        // 这里可以添加AI分析失败的UI提示
      } else if (message.type === 'email_sent') {
        // 邮件发送成功提示和聊天室分类更新
        const emailData = message.data;
        console.log('✅ 邮件发送成功:', emailData);
        
        // 立即更新聊天室分类 - 将收件人移动到"已回复"分类
        const userEmail = emailData.to;
        
        // 检查是否为重复事件（与new_sent_email使用相同的事件键）
        const eventKey = `sent_email_${userEmail}_${emailData.message_id || emailData.id}`;
        if (isEventProcessed(eventKey)) {
          console.log(`⚠️ 跳过重复的邮件发送成功事件: ${userEmail} (事件键: ${eventKey})`);
          return; // 直接跳过处理
        }
        console.log(`✅ 新的邮件发送成功事件，继续处理: ${userEmail} (事件键: ${eventKey})`); // 收件人邮箱
        
        setChatRooms(prevRooms => {
          const newRooms = { ...prevRooms };
          
          // 查找是否已存在该用户的聊天室
          let existingRoom: ChatRoom | null = null;
          let existingCategory: keyof ChatRoomsResponse | null = null;
          
          console.log(`🔍 邮件发送成功，查找现有聊天室: ${userEmail}`);
          
          // 查找现有聊天室
          const allCategories: (keyof ChatRoomsResponse)[] = ['unreplied', 'replied', 'has_new'];
          
          for (const category of allCategories) {
            const rooms = newRooms[category] || [];
            const room = rooms.find(r => r.user_email === userEmail);
            
            if (room) {
              existingRoom = room;
              existingCategory = category;
              break;
            }
          }
          
          if (existingRoom && existingCategory) {
            console.log(`✅ 找到现有聊天室: ${userEmail} 在 ${existingCategory} 分类`);
            
            // 更新聊天室信息
            const updatedRoom = {
              ...existingRoom,
              reply_emails_count: (existingRoom.reply_emails_count || 0) + 1,
              latest_reply_email_time: new Date().toISOString(),
              update_time: new Date().toISOString()
            };
            
            // 移除旧位置的聊天室
            newRooms[existingCategory] = newRooms[existingCategory].filter(r => r.user_email !== userEmail);
            
            // 移动到"已回复"分类
            const newCategory: keyof ChatRoomsResponse = 'replied';
            
            // 确保新分类中不存在重复条目
            const existsInNewCategory = newRooms[newCategory].some(r => r.user_email === userEmail);
            if (!existsInNewCategory) {
              newRooms[newCategory] = [updatedRoom, ...newRooms[newCategory]];
              console.log(`✅ 聊天室已移动: ${userEmail} 从 ${existingCategory} 移到 ${newCategory}`);
              
              // SSE更新时不自动滚动，避免打断用户浏览
            } else {
              console.warn(`⚠️ 目标分类 ${newCategory} 中已存在聊天室: ${userEmail}，跳过添加`);
              // 如果目标分类已存在，至少更新现有条目
              const existingIndex = newRooms[newCategory].findIndex(r => r.user_email === userEmail);
              if (existingIndex !== -1) {
                newRooms[newCategory][existingIndex] = updatedRoom;
                console.log(`✅ 更新现有聊天室: ${userEmail} 在 ${newCategory} 分类中`);
              }
            }
          } else {
            console.log(`⚠️ 未找到现有聊天室: ${userEmail}，等待IMAP同步创建`);
          }
          
          // 应用全局去重，确保没有重复条目
          return deduplicateAllChatRooms(newRooms);
        });
      }
    }
  });

  // 调试信息
  console.log('=== ChatRoomPage 渲染状态 ===');
  console.log('Loading:', loading);
  console.log('Error:', error);
  console.log('ChatRooms:', chatRooms);
  console.log('Unreplied count:', chatRooms?.unreplied?.length);
  console.log('Replied count:', chatRooms?.replied?.length);
  console.log('Has new count:', chatRooms?.has_new?.length);
  console.log('Sidebar width:', sidebarWidth);
  console.log('=================================');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">加载...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-red-600 mb-2">⚠️ {error}</div>
          <button
            onClick={loadChatRooms}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full theme-bg-secondary select-none">
      {/* 左侧聊天室列表 - 可调整宽度 */}
      <div 
        ref={sidebarRef}
        className="theme-bg-primary border-r theme-border-primary flex flex-col"
        style={{ 
          width: `${sidebarWidth}px`, 
          minWidth: '200px', 
          maxWidth: '600px',
          flexShrink: 0 
        }}
      >
        <div className="p-4 border-b theme-border-primary">
          <h2 className="text-lg font-semibold theme-text-primary">用户反馈</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <ChatRoomList
            chatRooms={chatRooms}
            selectedUserEmail={selectedUserEmail}
            onSelectChatRoom={handleSelectChatRoom}
            onRefresh={loadChatRooms}
            onUpdateChatRoom={handleUpdateChatRoom}
            ref={chatRoomListRef}
          />
        </div>
      </div>

      {/* 拖拽调整宽度的分隔条 */}
      <div 
        ref={resizerRef}
        className={`w-1 h-full z-10 theme-resizer ${
            isResizing ? 'resizing' : ''
          }`}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          title="拖拽调整宽度，双击重置为默认宽度(320px)"
        >
          {/* 拖拽条的视觉指示器 */}
          <div className="theme-resizer-handle"></div>
          
          {/* 拖拽区域扩展（增加可拖拽区域） */}
          <div className="theme-resizer-area"></div>
        </div>

      {/* 右侧聊天室对话 */}
      <div 
        ref={contentRef}
        className="flex-1 flex flex-col relative"
        style={{ 
          width: `calc(100% - ${sidebarWidth}px - 4px)` 
        }}
      >

        {selectedUserEmail ? (
          <ChatRoomView />
        ) : (
          <div className="flex-1 flex items-center justify-center theme-bg-secondary">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 theme-bg-tertiary rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 theme-text-tertiary" />
              </div>
              <h3 className="text-lg font-medium theme-text-primary mb-2">选择一个聊天室</h3>
              <p className="theme-text-secondary">从左侧列表中选择一个聊天室开始查看对话</p>
            </div>
          </div>
        )}
      </div>

      {/* 拖拽时的遮罩层，防止iframe等元素干扰 */}
      {isResizing && (
        <div className="fixed inset-0 z-50 cursor-col-resize bg-black bg-opacity-5" />
      )}
    </div>
  );
};

export default ChatRoomPage; 